/**
 * Directive to handle image load errors and display initials avatar
 * Usage: <img [src]="imageUrl" appDefaultImage [userName]="user.name">
 */
import { Directive, ElementRef, HostListener, Input, inject, Renderer2 } from '@angular/core';

@Directive({
  selector: 'img[appDefaultImage]',
  standalone: true
})
export class DefaultImageDirective {
  @Input() userName = '';
  @Input() defaultImage = '/assets/default-avatar.svg';

  private readonly el = inject(ElementRef<HTMLImageElement>);
  private readonly renderer = inject(Renderer2);

  @HostListener('error')
  onError(): void {
    const img = this.el.nativeElement;

    // Try to get name from alt attribute if userName not provided
    const name = this.userName || img.alt || '';

    if (name) {
      // Generate initials avatar
      const initials = this.getInitials(name);
      const avatarUrl = this.generateInitialsAvatar(initials);
      img.src = avatarUrl;
    } else {
      // Fallback to default image if no name available
      img.src = this.defaultImage;
    }

    img.onerror = null; // Prevent infinite loop
  }

  /**
   * Extract initials from full name
   */
  private getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(p => p.length > 0);

    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

    // Get first letter of first name and first letter of last name
    const firstInitial = parts[0].charAt(0).toUpperCase();
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();

    return firstInitial + lastInitial;
  }

  /**
   * Generate a data URL for an avatar with initials
   */
  private generateInitialsAvatar(initials: string): string {
    const canvas = document.createElement('canvas');
    const size = 120; // Higher resolution for better quality
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return this.defaultImage;

    // Background color based on initials (consistent color for same initials)
    const colors = [
      '#7b1fa2', // Purple
      '#1976d2', // Blue
      '#388e3c', // Green
      '#f57c00', // Orange
      '#c2185b', // Pink
      '#0097a7', // Cyan
      '#5d4037', // Brown
      '#455a64', // Blue Grey
    ];

    const colorIndex = initials.charCodeAt(0) % colors.length;
    const bgColor = colors[colorIndex];

    // Draw background circle
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();

    // Draw initials
    ctx.fillStyle = '#ffffff';
    ctx.font = `${size * 0.4}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, size / 2, size / 2);

    return canvas.toDataURL('image/png');
  }
}

